import { createFileRoute, redirect } from "@tanstack/react-router";
import useTranslation from "../../hooks/useTranslation";
import {
  getAllPatternUnlocks,
  type PatternUnlock,
} from "../../features/patternUnlocks/patternUnlocks";
import { resolveLocalizedPatternUnlock } from "../../features/patternUnlocks/patternUnlockLocalization";
import { getUnlockedPatternIds } from "../../features/patternUnlocks/patternUnlockStorage";
import { PATTERN_UNLOCKS_MVP_VISIBLE } from "../../features/patternUnlocks/patternUnlockVisibility";

export const Route = createFileRoute("/app/language_insights")({
  beforeLoad: () => {
    if (!PATTERN_UNLOCKS_MVP_VISIBLE) {
      throw redirect({ to: "/app/reeprise", search: { mobileView: undefined } });
    }
  },
  component: LanguageInsightsPage,
});

function LanguageInsightsPage() {
  const { translate } = useTranslation();
  const unlockedIds = getUnlockedPatternIds();
  const unlocked = getAllPatternUnlocks().filter((pattern) =>
    unlockedIds.includes(pattern.id),
  );

  return (
    <div className="space-y-4 px-3 py-4 md:px-4 md:py-6">
      <header className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {translate("language_insights_label")}
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 md:text-3xl">
          {translate("language_insights_title")}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {translate("language_insights_description")}
        </p>
        <div
          className="mt-4 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700"
          data-testid="language-insights-explainer"
        >
          <p>{translate("language_insights_explainer_primary")}</p>
          <p>{translate("language_insights_explainer_secondary")}</p>
        </div>
      </header>

      {unlocked.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-600">
          <p className="font-medium text-slate-800">
            {translate("language_insights_empty")}
          </p>
          <p className="mt-2">{translate("language_insights_empty_detail")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {unlocked.map((pattern) => (
            <PatternCard key={pattern.id} pattern={pattern} />
          ))}
        </div>
      )}
    </div>
  );
}

function PatternCard({ pattern }: { pattern: PatternUnlock }) {
  const { translate } = useTranslation();
  const localizedPattern = resolveLocalizedPatternUnlock(pattern, translate);

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {localizedPattern.title}
      </p>
      <h2 className="mt-1 text-lg font-semibold text-slate-900">
        {localizedPattern.pattern}
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        {localizedPattern.shortExplanation}
      </p>
      {localizedPattern.contextPattern && (
        <p className="mt-2 text-xs text-slate-600">
          {localizedPattern.contextPattern}
        </p>
      )}
      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-slate-700">
        {localizedPattern.examples.map((example) => (
          <li key={`${example.input}-${example.output ?? ""}`}>
            {example.output
              ? `${example.input} -> ${example.output} (${example.meaning})`
              : `${example.input} (${example.meaning})`}
          </li>
        ))}
      </ul>
    </article>
  );
}
